<template>
  <div class="w-100">
    <brb-input-group
      v-bind="inputGroupAttrs"
      :prepend="label"
      :append="append"
      :size="size"
      :required="required"
      class="flex-nowrap"
    >
      <template v-if="$slots.prepend" #prepend>
        <slot name="prepend"></slot>
      </template>

      <template v-if="$slots.append" #append>
        <slot name="append"></slot>
      </template>

      <vue-multiselect
        v-model="model"
        v-bind="$attrs"
        :options="computedOptions"
        :class="{ miniMulti: size === 'sm' }"
        :loading="loading"
        :track-by="trackBy"
        :label="optionLabel"
        :multiple="multiple"
        :placeholder="
          model
            ? $attrs.multiple
              ? $t('forms.choose')
              : model[optionLabel]
            : $t('forms.choose')
        "
        :show-labels="showLabels"
        :allow-empty="allowEmpty"
        @open="onMultiselectOpen"
        @select="$emit('select', $event)"
        class="flex-grow-1 mw-100"
      >
        <template #option="{ option }">
          <div class="d-flex align-items-center justify-content-between">
            <span>{{ option[optionLabel] }}</span>

            <span
              v-if="optionTags.includes(option[trackBy])"
              v-bind="optionTagBind"
            >
              {{ optionTagText }}
            </span>
          </div>
        </template>

        <template #single-label>
          <span v-if="model">{{ model[optionLabel] }}</span>
        </template>

        <template #placeholder>
          <span class="placeholder">{{ placeholder }}</span>
        </template>

        <template #noResult>
          <span>{{ noResult }}</span>
        </template>

        <template #noOptions>
          <span>{{ noOptions }}</span>
        </template>

        <template #tag="{ option, remove }">
          <div
            class="d-flex align-items-center bg-success rounded px-1 flex-shrink-0"
          >
            <span>
              {{ option[optionLabel] }}
            </span>

            <brb-btn
              class="px-1 text-white"
              variant="transparent"
              @click="remove(option)"
            >
              <font-awesome-icon icon="times" class="mx-1" size="xs" />
            </brb-btn>
          </div>
        </template>
      </vue-multiselect>
    </brb-input-group>

    <span
      v-if="error && typeof error === 'string'"
      class="d-block text-right text-danger small mt-2"
    >
      {{ error }}
    </span>
  </div>
</template>

<script>
import VueMultiselect from 'vue-multiselect'
import '@/assets/scss/vueselect.scss'

export default {
  name: 'BrbSelect',
  components: {
    BrbInputGroup: () => import('./BrbInputGroup.vue'),
    VueMultiselect
  },
  props: {
    value: {
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    exclude: {
      type: Array,
      default: () => []
    },
    inputGroupAttrs: Object,
    label: String,
    append: String,
    size: {
      type: String,
      default: 'md'
    },
    searchApi: Object,
    searchAction: {
      type: String,
      default: 'list'
    },
    searchFilter: {
      type: Object,
      default: () => {
        return {}
      }
    },
    searchMapCallback: {
      type: Function,
      default: null
    },
    searchFilterFn: {
      type: Function,
      default: null
    },
    trackBy: {
      type: String,
      default: 'value'
    },
    optionLabel: {
      type: String,
      default: 'text'
    },
    noResult: {
      type: String,
      default: function () {
        return this.$t('forms.nothingFound')
      }
    },
    noOptions: {
      type: String,
      default: function () {
        return this.$t('shared.nothingAdded')
      }
    },
    optionTags: {
      type: Array,
      default: () => []
    },
    optionTagText: String,
    optionTagBind: {
      type: Object,
      default: () => {
        return {
          class: 'text-success small'
        }
      }
    },
    error: [Boolean, String],
    placeholder: String,
    showLabels: Boolean,
    prependPlaceholder: Boolean,
    prependPlaceholderText: {
      type: String,
      default: function () {
        return this.$t('shared.Choose')
      }
    },
    allowEmpty: Boolean,
    multiple: Boolean,
    required: Boolean,
    objectOutput: Boolean
  },
  data() {
    return {
      loading: false,
      items: []
    }
  },
  computed: {
    model: {
      get() {
        return this.value !== null
          ? this.multiple
            ? Array.isArray(this.value)
              ? this.allOptions.filter((option) =>
                  (this.objectOutput
                    ? this.value[this.trackBy]
                    : this.value
                  ).includes(option[this.trackBy])
                )
              : this.objectOutput
              ? this.value[this.trackBy]
              : this.value
            : this.allOptions.find(
                (option) =>
                  option[this.trackBy] ==
                  (this.objectOutput ? this.value[this.trackBy] : this.value)
              )
          : this.allOptions.find((option) => option[this.trackBy] === null) ||
              this.value
      },
      set(value) {
        const output =
          value !== null
            ? this.multiple
              ? value.map((option) =>
                  this.objectOutput ? option : option[this.trackBy]
                )
              : this.objectOutput
              ? value
              : value[this.trackBy]
            : value
        this.$emit('input', output)
      }
    },
    allOptions() {
      return [
        ...(this.prependPlaceholder
          ? [
              {
                [this.trackBy]: null,
                [this.optionLabel]: this.prependPlaceholderText
              }
            ]
          : []),
        ...(this.options
          ? this.options.map((item) => {
              return {
                ...item,
                [this.optionLabel]: this.$te(item[this.optionLabel])
                  ? this.$t(item[this.optionLabel])
                  : item[this.optionLabel]
              }
            })
          : []),
        ...(this.items || [])
      ]
    },
    computedOptions() {
      let options = [...this.allOptions]

      if (this.exclude && this.exclude.length) {
        options = options.filter(
          (option) => !this.exclude.includes(option[this.trackBy])
        )
      }

      return options
    },
    fetchCondition() {
      return (
        (!this.options || !this.options.length) &&
        (!this.items || !this.items.length) &&
        this.searchApi &&
        this.searchApi[this.searchAction]
      )
    }
  },
  watch: {
    value: {
      handler(value) {
        if (this.fetchCondition && value) this.search()
      },
      immediate: true
    }
  },
  methods: {
    onMultiselectOpen() {
      if (this.fetchCondition) {
        this.search()
      }
    },
    search() {
      this.loading = true
      return this.searchApi[this.searchAction](this.searchFilter)
        .then((response) => {
          this.items = response

          if (this.searchMapCallback) {
            this.items = response.map(this.searchMapCallback)
          }

          if (this.searchFilterFn) {
            this.items = this.items.filter(this.searchFilterFn)
          }

          return response
        })
        .catch((error) => this.$showError(error))
        .finally(() => (this.loading = false))
    },
    reset() {
      this.model = null
      if (this.searchApi && this.searchApi[this.searchAction]) {
        this.items = []
      }
    }
  }
}
</script>

<style>
.multiselect__tags {
  display: flex;
  align-items: center;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.multiselect__spinner {
  height: 90%;
}

.multiselect__input,
.multiselect__single {
  padding: 0 !important;
  margin: 0 !important;
}

.multiselect__select,
.multiselect__tags {
  height: 100%;
}
.multiselect__select {
  display: flex;
  justify-content: center;
  align-items: center;
}
.multiselect__select::before {
  margin: 0;
}

.multiselect__tags-wrap {
  display: flex;
  flex-wrap: wrap;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  gap: 0.25rem;
}

.multiselect__placeholder {
  margin-bottom: 0;
}
</style>
